import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import "./CVGeneration.css";

interface CVGenerationProps {
  candidateId: string | number;
  candidateName: string;
  onClose: () => void;
}

interface CVContent {
  content?: string;
  cvContent?: string;
  format?: string;
}

interface CVResponse {
  success?: boolean;
  data?: CVContent;
  format?: string;
  content?: string;
  cvContent?: string;
}

interface GenerateCVParams {
  candidateId: string | number;
  format: string;
}

interface EditCVParams {
  candidateId: string | number;
  prompt: string;
  currentCV: string;
}

interface EditCVResponse {
  success?: boolean;
  data?:
    | {
        content?: string;
      }
    | string;
}

const CVGeneration: React.FC<CVGenerationProps> = ({
  candidateId,
  candidateName,
  onClose,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [format, setFormat] = useState("html");
  const [generatedCV, setGeneratedCV] = useState<CVContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  // Prompt editor state
  const [promptText, setPromptText] = useState("");
  const [editedCV, setEditedCV] = useState<string | null>(null);
  const [originalCV, setOriginalCV] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Generate CV mutation
  const generateCVMutation = useMutation<CVResponse, Error, GenerateCVParams>(
    async ({ candidateId: cId, format: fmt }) => {
      const response = (await recruitingAPI.generateCV(cId, {
        format: fmt,
      })) as CVResponse;
      return response;
    },
    {
      onSuccess: (response) => {
        // Handle nested response structure: response.data contains the cvResult
        let cvData: CVContent | null = null;
        if (response.success && response.data) {
          // The response.data is the cvResult from backend which has { success, format, content, metadata }
          cvData = response.data;
        } else if (response.data?.content) {
          // Direct access if structure is different
          cvData = response.data;
        } else {
          setError(t("cvGeneration.failedToGenerate"));
          return;
        }

        setGeneratedCV(cvData);
        setError(null);

        // Store original CV for HTML format to enable reset
        const actualFormat = cvData.format || format;
        if (actualFormat === "html") {
          const cvContent = cvData.content || cvData.cvContent;
          const content =
            typeof cvContent === "string"
              ? cvContent
              : (cvContent as CVContent)?.content || cvContent;
          setOriginalCV(content as string);
          setEditedCV(null); // Reset edited CV when new CV is generated
          setPromptText(""); // Clear prompt
        }
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        console.error("Error generating CV:", err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            t("cvGeneration.failedToGenerate"),
        );
      },
    },
  );

  const handleGenerate = () => {
    setError(null);
    setGeneratedCV(null);
    generateCVMutation.mutate({ candidateId, format });
  };

  const handleDownload = () => {
    if (!generatedCV) return;

    const actualFormat = generatedCV.format || format;
    // Use edited CV if available, otherwise use original
    const cvContent = editedCV || generatedCV.content || generatedCV.cvContent;

    let content: string;
    let filename: string;
    let mimeType: string;

    const contentStr =
      typeof cvContent === "string"
        ? cvContent
        : (cvContent as CVContent)?.content || "";

    if (actualFormat === "html") {
      content = contentStr;
      filename = `${String(candidateName).replaceAll(/\s+/g, "_")}_CV${editedCV ? "_edited" : ""}.html`;
      mimeType = "text/html";
    } else if (actualFormat === "markdown") {
      content = contentStr;
      filename = `${String(candidateName).replaceAll(/\s+/g, "_")}_CV.md`;
      mimeType = "text/markdown";
    } else {
      content = contentStr;
      filename = `${String(candidateName).replaceAll(/\s+/g, "_")}_CV.txt`;
      mimeType = "text/plain";
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const actualFormat = generatedCV?.format || format;
    if (!generatedCV || actualFormat !== "html") {
      alert(t("cvGeneration.printHtmlOnly"));
      return;
    }

    // Use edited CV if available, otherwise use original
    const cvContent = editedCV || generatedCV.content || generatedCV.cvContent;
    const content =
      typeof cvContent === "string"
        ? cvContent
        : (cvContent as CVContent)?.content || "";

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const handlePreview = () => {
    if (format === "html") {
      setPreviewMode(!previewMode);
    } else {
      alert(t("cvGeneration.previewHtmlOnly"));
    }
  };

  // Edit CV mutation
  const editCVMutation = useMutation<EditCVResponse, Error, EditCVParams>(
    async ({ candidateId: cId, prompt, currentCV }) => {
      const response = (await recruitingAPI.editCV(
        cId,
        prompt,
        currentCV,
      )) as EditCVResponse;
      return response;
    },
    {
      onSuccess: (response) => {
        if (response.success && response.data) {
          const editedContent =
            typeof response.data === "string"
              ? response.data
              : response.data.content;
          setEditedCV(editedContent || null);
          setEditError(null);
          setPromptText(""); // Clear prompt after successful edit
        } else {
          setEditError(t("cvGeneration.failedToEdit"));
        }
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        console.error("Error editing CV:", err);
        setEditError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            t("cvGeneration.failedToEdit"),
        );
      },
    },
  );

  const handleApplyPrompt = () => {
    if (!promptText.trim()) {
      setEditError(t("cvGeneration.enterPrompt"));
      return;
    }

    const actualFormat = generatedCV?.format || format;
    if (actualFormat !== "html") {
      setEditError(t("cvGeneration.promptHtmlOnly"));
      return;
    }

    // Get current CV content (edited or original)
    const currentCVContent = editedCV || originalCV;
    if (!currentCVContent) {
      setEditError(t("cvGeneration.noContentToEdit"));
      return;
    }

    setEditError(null);
    editCVMutation.mutate({
      candidateId,
      prompt: promptText.trim(),
      currentCV: currentCVContent,
    });
  };

  const handleResetToOriginal = () => {
    setEditedCV(null);
    setPromptText("");
    setEditError(null);
  };

  const renderCVContent = () => {
    if (!generatedCV) return null;

    const actualFormat = generatedCV.format || format;
    // Use edited CV if available, otherwise use original
    const cvContent = editedCV || generatedCV.content || generatedCV.cvContent;
    const content =
      typeof cvContent === "string"
        ? cvContent
        : (cvContent as CVContent)?.content || "";

    if (actualFormat === "html") {
      if (previewMode) {
        return (
          <div className="cv-preview-container">
            <iframe
              srcDoc={content}
              title="CV Preview"
              className="cv-preview-iframe"
            />
          </div>
        );
      } else {
        return <pre className="cv-content-text">{content}</pre>;
      }
    } else {
      return <pre className="cv-content-text">{content}</pre>;
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      role="presentation"
    >
      <div
        className="modal large-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-generation-title"
      >
        <div className="modal-header">
          <h2 id="cv-generation-title">
            {t("cvGeneration.title", { name: candidateName })}
          </h2>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-content">
          <div className="cv-generation-content">
            {!generatedCV && (
              <>
                <div className="cv-format-selection">
                  <label htmlFor="format-select">
                    {t("cvGeneration.selectFormat")}
                  </label>
                  <select
                    id="format-select"
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    className="format-select"
                    disabled={generateCVMutation.isLoading}
                  >
                    <option value="html">{t("cvGeneration.html")}</option>
                    <option value="markdown">
                      {t("cvGeneration.markdown")}
                    </option>
                    <option value="text">{t("cvGeneration.plainText")}</option>
                  </select>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="cv-actions">
                  <button
                    onClick={handleGenerate}
                    disabled={generateCVMutation.isLoading}
                    className="btn-primary"
                  >
                    {generateCVMutation.isLoading
                      ? t("cvGeneration.generatingCV")
                      : t("cvGeneration.generateCV")}
                  </button>
                  <button onClick={onClose} className="btn-secondary">
                    {t("common:actions.cancel")}
                  </button>
                </div>

                {generateCVMutation.isLoading && (
                  <div className="loading-indicator">
                    <div className="spinner"></div>
                    <p>{t("cvGeneration.generatingMessage")}</p>
                  </div>
                )}
              </>
            )}

            {generatedCV && (
              <>
                <div className="cv-actions-header">
                  <div className="format-info">
                    {t("cvGeneration.format")}{" "}
                    <strong>
                      {(generatedCV.format || format).toUpperCase()}
                    </strong>
                  </div>
                  <div className="cv-action-buttons">
                    {(generatedCV.format || format) === "html" && (
                      <button
                        onClick={handlePreview}
                        className="btn btn-outline"
                      >
                        {previewMode
                          ? t("cvGeneration.showCode")
                          : t("cvGeneration.preview")}
                      </button>
                    )}
                    <button onClick={handleDownload} className="btn-primary">
                      {t("cvGeneration.download")}
                    </button>
                    {(generatedCV.format || format) === "html" && (
                      <button onClick={handlePrint} className="btn btn-outline">
                        {t("cvGeneration.print")}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setGeneratedCV(null);
                        setError(null);
                        setPreviewMode(false);
                        setEditedCV(null);
                        setOriginalCV(null);
                        setPromptText("");
                        setEditError(null);
                      }}
                      className="btn btn-outline"
                    >
                      {t("cvGeneration.generateNew")}
                    </button>
                    <button onClick={onClose} className="btn btn-secondary">
                      {t("common:actions.close")}
                    </button>
                  </div>
                </div>

                <div className="cv-content-container">{renderCVContent()}</div>

                {/* Prompt Editor - Only for HTML format */}
                {(generatedCV.format || format) === "html" && (
                  <div className="cv-prompt-editor">
                    <div className="prompt-editor-header">
                      <h3>{t("cvGeneration.editWithPrompt")}</h3>
                      <p className="prompt-editor-description">
                        {t("cvGeneration.promptDescription")}
                      </p>
                    </div>

                    {editError && (
                      <div className="error-message">{editError}</div>
                    )}

                    <div className="prompt-input-container">
                      <textarea
                        className="prompt-input"
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        placeholder={t("cvGeneration.promptPlaceholder")}
                        rows={4}
                        disabled={editCVMutation.isLoading}
                      />
                    </div>

                    <div className="prompt-actions">
                      <button
                        onClick={handleApplyPrompt}
                        disabled={
                          editCVMutation.isLoading || !promptText.trim()
                        }
                        className="btn-primary"
                      >
                        {editCVMutation.isLoading
                          ? t("cvGeneration.applyingChanges")
                          : t("cvGeneration.applyPrompt")}
                      </button>
                      {editedCV && (
                        <button
                          onClick={handleResetToOriginal}
                          disabled={editCVMutation.isLoading}
                          className="btn-outline"
                        >
                          {t("cvGeneration.resetToOriginal")}
                        </button>
                      )}
                    </div>

                    {editCVMutation.isLoading && (
                      <div className="prompt-loading">
                        <div className="spinner"></div>
                        <p>{t("cvGeneration.aiModifying")}</p>
                      </div>
                    )}

                    {editedCV && (
                      <div className="edit-success-message">
                        {t("cvGeneration.editSuccess")}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVGeneration;
